// The bookkeeping behind `NavController.callAsync` — the three value-picker pages you open,
// choose something in, and come straight back from (#4411).
//
// A call is one caller waiting for one callable page. The rules that get the answer back to
// the right caller at the right moment are small, easy to revert by accident, and were the
// site of a real bug: resolving at the wrong moment made a day picked in the task calendar
// silently do nothing on the Training and Nutrition pages. They used to live inside the
// Angular-injectable NavController, where no test could reach them — constructing one needs
// the whole injector plus a live nav component.
//
// So they live here instead, holding no Angular and no browser types, the same way
// nav-restore-coordination.ts and nav-stack-converter.ts already do: this module owns the
// map and the decisions, and nav-controller keeps the side effects (creating the promise,
// dropping the page's ephemeral params, and running the delivery).
//
// The behaviour this backs is documented in design-docs/navigation.md, "The other way a page
// returns a result".

/**
 * One in-flight call: how to answer the caller, plus the result the callable page recorded
 * on its way out.
 *
 * `hasReturnedResult` is a separate flag rather than a `result !== undefined` check, because
 * a page is allowed to return `undefined` as its answer, and that must still count as
 * "returned" rather than as a dismissal.
 */
interface PendingCall {
  resolve: (result: any) => void;
  returnedResult?: any;
  hasReturnedResult: boolean;
}

/**
 * What a caller is owed, handed back by `takeAnswer` once the entry has been removed.
 * `result` is already the final answer: what the page returned, or `null` when it went away
 * without returning anything.
 */
export interface CallAnswer {
  resolve: (result: any) => void;
  result: any;
}

/** The in-flight `callAsync` calls, and every operation on them. */
export class NavCallRegistry {
  private calls = new Map<string, PendingCall>();

  /**
   * Start waiting for a call's answer. `resolve` is what hands the result to the caller —
   * called at most once, and only by `takeAnswer`'s holder.
   */
  register(callId: string, resolve: (result: any) => void): void {
    this.calls.set(callId, { resolve, hasReturnedResult: false });
  }

  /**
   * Drop a call that never opened its page, so nothing can answer it later. Used where
   * `callAsync` gives up before pushing: the page filter redirected, or the app is reloading.
   * The caller is answered directly on those paths.
   */
  abandon(callId: string): void {
    this.calls.delete(callId);
  }

  /**
   * Write down the result a callable page is returning, without handing it over. Delivery
   * waits for the page's component to be destroyed — see `takeAnswer`.
   *
   * A missing entry is normal, not an error. Every path that removes the entry first
   * produces one: an abandoned call, or a page destroyed before it returned. In each case
   * the caller has already been answered or has gone away, so there is nothing left to
   * record against.
   */
  recordResult(callId: string, result: any): void {
    const pending = this.calls.get(callId);
    if (!pending) return;

    pending.returnedResult = result;
    pending.hasReturnedResult = true;
  }

  /**
   * Take the answer owed to a caller, and forget the call. Returns null when there is no
   * such call — it was abandoned, or already answered.
   *
   * Removing the entry and working out the answer are ONE operation on purpose. The entry
   * has to be gone before anybody resolves the caller's promise, and a delivery that runs
   * later (deferred behind a browser pop) must not be able to fire twice. Making that
   * structural is cheaper than leaving it as a rule somebody has to remember.
   *
   * A page that goes away without recording a result was dismissed, so the caller gets
   * `null` — the same answer a cancelled call has always produced.
   */
  takeAnswer(callId: string): CallAnswer | null {
    const pending = this.calls.get(callId);
    if (!pending) return null;

    this.calls.delete(callId);

    return {
      resolve: pending.resolve,
      result: pending.hasReturnedResult ? pending.returnedResult : null,
    };
  }
}

/**
 * Must a call's answer wait for the browser's back-handling to finish before it is handed
 * over? When the pop came from the browser, the popstate handler is still running: a
 * continuation that pushes a page from in there loses its history entry (pushState is
 * suppressed) and can be undone by the pop loop. So the delivery is queued until the handler
 * has finished — the same reason the returnCallback path defers.
 */
export function shouldDeferCallDelivery(state: { popCameFromBrowser: boolean }): boolean {
  return state.popCameFromBrowser;
}
